import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import {
  cloneObject,
  ConnectedClassIndicator,
  deserify,
  getLocalStorage,
  IStyles,
  PaginationButton,
  QuestionContainerWithSolution,
  getTeacherSubjectName,
  theme,
  useCommonServiceClientContext,
  pxToRem,
  pxTovW,
  ToggleSwitch,
  Loader,
} from '@geneo2-web/shared-ui';
import { useAppDispatch, useAppSelector } from '../../../reduxStore/reduxHooks';
import { Question } from '@protos/content_management/content.db_pb';
import {
  isBaseStationEffectivelyOnline,
  isQuestionSessionMatched,
  useClickerAssessmentContext,
} from '../../../app/Context/ClickerAssessmentContextProvider';
import { Timestamp } from '@bufbuild/protobuf';
import {
  AssignmentConfigEnum,
  SessionStatusEnum,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import {
  setClickerAssessmentInfo,
  setQuestionIndex,
  setQuestionSessionDetails,
} from './reducer/clickerAssessment.slice';
import { useNavigate } from 'react-router-dom';
import { HOME } from '../../../routeHandling/RoutesNomenclature';
import { ResponseList } from './LeftPanel/ResponseList';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import { ConnectedClassIndicatorWithDetails } from './LeftPanel/ConnectedClassIndicatorWithDetails';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import { TimerWithMinSec } from '../components/TimerWithMinSec';
import { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';

const styles: IStyles = {
  root: {
    // paddingX: { xs: pxToRem(0), md: pxToRem(20), lg: pxTovW(240) },
    paddingX: { xs: pxToRem(0), md: pxToRem(20) },
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: 'space-between',
    gap: { xs: pxToRem(0), md: pxTovW(30) },
  },
  sectionNameText: {
    marginBottom: { xs: pxToRem(10), md: pxTovW(20) },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
  },
};
const ASSESSMENT_SOUND_PATHS = {
  clockTickingDown: '/assets/shared-ui/sounds/clock-ticking-down.mp3',
  questionReveal: '/assets/shared-ui/sounds/question-reveal.mp3',
};
export default function ClickerAssessment() {
  const tickingAudioRef = useRef<HTMLAudioElement | null>(null);
  const questionRevealAudioRef = useRef<HTMLAudioElement | null>(null);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const largeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
  const { setSelectedFunction, isIpadOnly, setSelectedLogoClickHandler } =
    useGlobalContext();
  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'loading'
  );
  const dispatch = useAppDispatch();
  const [showResponse, setShowResponse] = useState(false);
  const [timer, setTimer] = useState(30); // Default 30 seconds
  const { current_assessmemt_classStats, selected_assessment_id } = deserify(
    useAppSelector((state) => state.manageHomework)
  );
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const { LmsClickerAssessmentTeacherAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const {
    assessment_content,
    current_question_index,
    clicker_assessment_session_data,
    ca_question_session_details,
    teacher_assessment_session_details,
    ca_base_station_details,
    student_list,
    clicker_assessment_info,
  } = deserify(useAppSelector((state) => state.clickerAssessment));
  const teacher_id = getLocalStorage('userId');
  const navigate = useNavigate();
  const {
    upsertConnectedClassQuestionSessionApiv2,
    unsubscribeQuestionFromFirestore,
    unsubscribeFromFirestore,
    questionSessionData,
    caBaseStationData,
    upsertConnectedClassSessionApiv2,
    upsertConnectedClassSessionApiV3,
    resetClickerAssessment,
  } = useClickerAssessmentContext();

  const isEffectivelyOnline = isBaseStationEffectivelyOnline(
    caBaseStationData,
    current_assessmemt_classStats?.assessment?.classId,
    current_assessmemt_classStats?.assessment?.sectionId
  );

  const isQuestionSessionReady = isQuestionSessionMatched(
    ca_question_session_details?.question_session_id,
    questionSessionData,
    caBaseStationData
  );

  const isQuestionBasedTimerEnabled =
    current_assessmemt_classStats?.assessment?.assignmentInfo.find(
      (assignment) =>
        assignment.configName ===
        AssignmentConfigEnum.ASSIGNMENT_CONFIG_QUESTION_BASED_TIMER
    )?.isEnabled;

  useEffect(() => {
    tickingAudioRef.current = new Audio(
      ASSESSMENT_SOUND_PATHS.clockTickingDown
    );

    questionRevealAudioRef.current = new Audio(
      ASSESSMENT_SOUND_PATHS.questionReveal
    );

    return () => {
      tickingAudioRef.current?.pause();
      questionRevealAudioRef.current?.pause();

      tickingAudioRef.current = null;
      questionRevealAudioRef.current = null;
    };
  }, []);
  useEffect(() => {
    if (!isQuestionBasedTimerEnabled) return;

    if (timer === 5) {
      const audio = tickingAudioRef.current;

      if (audio) {
        audio.currentTime = 0;
        audio.play().catch(() => {});
      }
    }

    if (timer === 0) {
      const audio = tickingAudioRef.current;

      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    }
  }, [timer, isQuestionBasedTimerEnabled]);

  // useEffect(() => {
  //   if (assessment_content) {
  //     startQuestionSession();
  //   }
  // }, [current_question_index]);
  useEffect(() => {
    const stopTickingSound = () => {
      const audio = tickingAudioRef.current;

      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };

    const playQuestionReveal = () => {
      const audio = questionRevealAudioRef.current;

      if (audio) {
        audio.currentTime = 0;
        audio.play().catch(() => {});
      }
    };

    stopTickingSound();

    // small gap so sounds never overlap
    const revealTimeoutId = setTimeout(() => {
      playQuestionReveal();
    }, 150);

    startQuestionSession();

    return () => {
      clearTimeout(revealTimeoutId);
    };
  }, [current_question_index]);

  const backButtonClickHandler = useCallback(
    async (navigateType = 'default') => {
      // if (current_question_index) {
      await endQuestionSession();
      // }
      resetClickerAssessment();
      if (navigateType === 'default') {
        navigate('/clicker-assessment-manage');
      } else {
        navigate(HOME);
      }
    },
    [
      //dependecy array with payload of endQuestionSession button
      current_question_index,
      ca_question_session_details?.question_session_id,
      teacher_assessment_session_details?.teacherAssessmentSessionId,
      // assessment_content,
    ]
  );

  useEffect(() => {
    setSelectedFunction(
      () => async () => await backButtonClickHandler('default')
    );
    setSelectedLogoClickHandler(
      () => async () => await backButtonClickHandler('logo')
    );
    return () => {
      setSelectedFunction(null);
      setSelectedLogoClickHandler(null);
    };
  }, [backButtonClickHandler]);

  const startQuestionSession = async () => {
    try {
      setLoading('loading');
      const activeQuestion =
        assessment_content?.assessmentContent[current_question_index];
      if (!activeQuestion?.questionId) {
        return;
      }
      const res = await upsertConnectedClassQuestionSessionApiv2({
        teacherAssessmentSessionId:
          teacher_assessment_session_details?.teacherAssessmentSessionId,
        questionId: activeQuestion?.questionId,
        sequence:
          assessment_content?.questionsSequenceInfo?.taskQuestionInfo.find(
            (taskInfo) => taskInfo.questionId === activeQuestion?.questionId
          )?.sequence,
        startTime: Timestamp.fromDate(new Date()),
        sessionStatus: SessionStatusEnum.SESSION_STATUS_STARTED,
      });
      if (res) {
        dispatch(
          setQuestionSessionDetails({
            question_session_id: res.connectedClassQuestionSessionId,
            question_id: activeQuestion?.questionId,
          })
        );
        // setLoading('completed');
      } else {
        dispatch(setQuestionSessionDetails(undefined));
        // setLoading('error');
        dispatch(
          setToastInfo({
            label: 'Error starting question session',
            variant: 'error',
            open: true,
          })
        );
      }
      setLoading('completed');
    } catch (err) {
      console.log(err);
      setLoading('error');
    }
  };
  const endQuestionSession = async (paginationType?: 'next' | 'prev') => {
    try {
      setLoading('loading');
      console.log('ending question session');
      const activeQuestion =
        assessment_content?.assessmentContent[current_question_index];
      const res = await upsertConnectedClassQuestionSessionApiv2({
        connectedClassQuestionSessionId:
          ca_question_session_details?.question_session_id,
        teacherAssessmentSessionId:
          teacher_assessment_session_details?.teacherAssessmentSessionId,
        questionId: activeQuestion?.questionId,
        sequence:
          assessment_content?.questionsSequenceInfo?.taskQuestionInfo.find(
            (taskInfo) => taskInfo.questionId === activeQuestion?.questionId
          )?.sequence,
        startTime: Timestamp.fromDate(new Date()),
        sessionStatus: paginationType
          ? SessionStatusEnum.SESSION_STATUS_COMPLETED
          : SessionStatusEnum.SESSION_STATUS_PAUSED,
      });
      if (res) {
        unsubscribeQuestionFromFirestore();
        const isLastQuestion =
          assessment_content &&
          paginationType === 'next' &&
          current_question_index ===
            assessment_content?.assessmentContent.length - 1;
        if (isLastQuestion) {
          await LmsClickerAssessmentTeacherAPIServiceV1ClientWithStatusCodeHandler.updateTeacherAssessmentSession(
            {
              teacherId: BigInt(teacher_id),
              assessmentId: selected_assessment_id,
              sessionStatus: SessionStatusEnum.SESSION_STATUS_COMPLETED,
              teacherAssessmentSessionId:
                teacher_assessment_session_details?.teacherAssessmentSessionId,
            }
          );
          await upsertConnectedClassSessionApiV3({
            classId: current_assessmemt_classStats?.assessment?.classId,
            sectionId: current_assessmemt_classStats?.assessment?.sectionId,
            // subject: current_assessmemt_classStats?.assessment?.subject,
            baseReceiverId: ca_base_station_details?.baseReceiverId,
            teacherId: teacher_id,
            sessionStatus: SessionStatusEnum.SESSION_STATUS_COMPLETED,
            connectedClassSessionId:
              clicker_assessment_session_data?.connectedClassSessionId,
            referenceId: selected_assessment_id,
          });
          unsubscribeFromFirestore();
          dispatch(setQuestionSessionDetails(undefined));
          // navigate(HOME);
          // dispatch(setSelectedAssessmentId(homeworkId));
          resetClickerAssessment();
          dispatch(
            setClickerAssessmentInfo({
              ...clicker_assessment_info,
              selected_attempt: undefined,
            })
          );
          navigate('/clicker-assessment-manage');
          return;
        }
        if (paginationType === 'next') {
          // await LmsClickerAssessmentTeacherAPIServiceV1ClientWithStatusCodeHandler.updateTeacherAssessmentSession(
          //   {
          //     teacherId: BigInt(teacher_id),
          //     assessmentId: selected_assessment_id,
          //     sessionStatus: SessionStatusEnum.SESSION_STATUS_ONGOING,
          //     teacherAssessmentSessionId:
          //       teacher_assessment_session_details?.teacherAssessmentSessionId,
          //   }
          // );
          dispatch(setQuestionIndex(current_question_index + 1));
        } else if (paginationType === 'prev') {
          dispatch(setQuestionIndex(current_question_index - 1));
        } else {
          //case of exit directlty
          await LmsClickerAssessmentTeacherAPIServiceV1ClientWithStatusCodeHandler.updateTeacherAssessmentSession(
            {
              teacherId: BigInt(teacher_id),
              assessmentId: selected_assessment_id,
              sessionStatus: SessionStatusEnum.SESSION_STATUS_PAUSED,
              teacherAssessmentSessionId:
                teacher_assessment_session_details?.teacherAssessmentSessionId,
            }
          );
          await upsertConnectedClassSessionApiV3({
            classId: current_assessmemt_classStats?.assessment?.classId,
            sectionId: current_assessmemt_classStats?.assessment?.sectionId,
            // subject: current_assessmemt_classStats?.assessment?.subject,
            baseReceiverId: ca_base_station_details?.baseReceiverId,
            teacherId: teacher_id,
            // lessonId: lesson_id,
            // schoolId: current_assessmemt_classStats?.assessment.sch,
            sessionStatus: SessionStatusEnum.SESSION_STATUS_PAUSED,
            connectedClassSessionId:
              clicker_assessment_session_data?.connectedClassSessionId,
            referenceId: selected_assessment_id,
          });
          unsubscribeFromFirestore();
        }
        dispatch(setQuestionSessionDetails(undefined));
        // setLoading('completed');
      } else {
        // setLoading('error');
        dispatch(
          setToastInfo({
            label: 'Error ending question session',
            variant: 'error',
            open: true,
          })
        );
      }
      setLoading('completed');
    } catch (err) {
      console.log(err);
      setLoading('error');
      dispatch(
        setToastInfo({
          label: 'Error ending question session',
          variant: 'error',
          open: true,
        })
      );
    }
  };
  console.log('ca_question_session_details', ca_question_session_details);
  const onNextClick = () => {
    if (assessment_content && current_question_index !== undefined) {
      endQuestionSession('next');
    }
  };
  const onPreviousClick = () => {
    if (current_question_index !== undefined && current_question_index > 0) {
      endQuestionSession('prev');
    }
  };
  const getOnlineKeyPadsCount = () => {
    const keyPads = caBaseStationData?.keyPads || [];
    const onlineKeyPadsCount = keyPads.filter(
      (keypad) => keypad.isOnline
    ).length;
    const totalKeyPadsCount = caBaseStationData?.keyPads.length || 0;
    return { onlineKeyPadsCount, totalKeyPadsCount };
  };
  if (loading === 'loading') {
    <Loader />;
  }
  if (!assessment_content) {
    return <Typography variant="h2"> NO QUESTIONS AVAILABLE</Typography>;
  }

  // ------------------------------------------------------------

  const getMultiSubjectSectionForQuestionId = (props: {
    assessmentContent: AssessmentContent;
    questionId: string | undefined;
  }) => {
    const { assessmentContent, questionId } = props;
    if (!questionId) return undefined;

    const qId = String(questionId);
    const sections = assessmentContent?.multiSubjectSections ?? [];
    for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
      const section = sections[sectionIndex];
      const subSections = section?.multiSubjectSubSections ?? [];
      for (const subSection of subSections) {
        const metaData = subSection?.subSectionMetaData ?? [];
        for (const meta of metaData) {
          if (meta?.questionId != null && String(meta.questionId) === qId) {
            return { section, sectionIndex };
          }
          const alternates = meta?.alternateQuestions ?? [];
          if (
            alternates?.some(
              (alt: any) =>
                alt?.questionId != null && String(alt.questionId) === qId
            )
          ) {
            return { section, sectionIndex };
          }
        }
      }
    }
    return undefined;
  };

  // ------------------------------------------------------------

  const getSectionText = () => {
    const sectionForQuestion = getMultiSubjectSectionForQuestionId({
      assessmentContent: assessment_content,
      questionId:
        assessment_content?.assessmentContent[current_question_index]
          ?.questionId,
    });
    const subjectName = getTeacherSubjectName(
      sectionForQuestion?.section.subjectId,
      user_info?.teachClassSubjects
    );
    const sectionTitle = sectionForQuestion?.section.sectionTitle;
    return subjectName + (sectionTitle ? ` - ${sectionTitle}` : '');
  };

  // ------------------------------------------------------------

  const studentList = student_list?.map((student) => {
    const response = questionSessionData?.responses.find(
      (e) => e.keypadSn === student.clickerSerialNo
    );
    return {
      ...student,
      response: response, // Add the response data to each student object
    };
  });

  // ------------------------------------------------------------

  // Timer countdown effect
  useEffect(() => {
    if (!isQuestionBasedTimerEnabled || timer <= 0) return;

    const interval = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer <= 1) {
          clearInterval(interval);
          onNextClick();
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [isQuestionBasedTimerEnabled, timer]);

  // Reset timer when question changes
  useEffect(() => {
    if (isQuestionBasedTimerEnabled) {
      setTimer(
        Math.trunc(
          (assessment_content.questionsSequenceInfo?.taskQuestionInfo.find(
            (taskInfo) =>
              taskInfo.questionId ===
              assessment_content?.assessmentContent[current_question_index]
                ?.questionId
          )?.estimatedTimeInMin ?? 0) * 60
        )
      );
    } else {
      setTimer(0);
    }
  }, [current_question_index, isQuestionBasedTimerEnabled]);

  // ------------------------------------------------------------

  return (
    <Box>
      <ConnectedClassIndicatorWithDetails
        activeQuestion={
          assessment_content?.assessmentContent[current_question_index]
        }
        showResponse={showResponse}
        isQuestionSessionLoading={loading === 'loading'}
      />

      <Box sx={{ ...styles.root, paddingTop: pxToRem(20) }}>
        <Box
          sx={{
            width: { xs: '95%', md: pxTovW(472) },
            order: { xs: '2', md: '1' },
            margin: 'auto',
            mt: { xs: pxToRem(10), md: 'unset' },
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: { md: pxTovW(5) },
              marginBottom: { xs: pxToRem(16), md: pxTovW(16) },
              background: 'white',
              p: { xs: pxToRem(5), md: 'unset' },
            }}
          >
            <Typography
              variant={
                isMobile ? 'h2' : isIpad ? 'h3' : largeScreen ? 'h2' : 'h2'
              }
              fontWeight="medium"
            >
              View Responses
            </Typography>
            <ToggleSwitch
              checked={showResponse}
              handleChange={() => setShowResponse(!showResponse)}
            />
          </Box>
          <ResponseList
            showResponse={showResponse}
            question={
              assessment_content?.assessmentContent[current_question_index]
            }
            students_data={student_list}
            questionSessionData={questionSessionData}
            baseStationData={caBaseStationData}
          />
        </Box>

        <Box
          sx={{
            // width: { xs: '95%', md: pxTovW(840) },
            width: { xs: '95%', md: 'unset' },
            flexGrow: 1,
            order: { xs: '1', md: '2' },
            display: 'flex',
            flexDirection: 'column',
            marginX: 'auto',
          }}
        >
          {isQuestionBasedTimerEnabled ? (
            <TimerWithMinSec
              timer={timer}
              totalTime={Math.trunc(
                (assessment_content.questionsSequenceInfo?.taskQuestionInfo.find(
                  (taskInfo) =>
                    taskInfo.questionId ===
                    assessment_content?.assessmentContent[
                      current_question_index
                    ]?.questionId
                )?.estimatedTimeInMin ?? 0) * 60
              )}
            />
          ) : null}

          {assessment_content?.assessment?.taskType ===
            TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM ||
          assessment_content?.assessment?.taskType ===
            TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_AUTOMATED ? (
            <Typography
              variant="h2"
              color="primary.main"
              textAlign="center"
              sx={styles.sectionNameText}
            >
              {getSectionText()}
            </Typography>
          ) : null}

          <QuestionContainerWithSolution
            questionTypeDisplay={true}
            // userAttemptedAnswer={all_answers}
            questionNumber={current_question_index + 1}
            question={
              new Question(
                assessment_content?.assessmentContent[current_question_index]
              )
            }
            // handleAnswerChange={handleAnswerChange}
            // handleNext={handleNext}
            // showNextButton
            // showHintIcon
            homeworkAttempt={true}
            // scanClickHandler={scanClickHandler}
            // selected_resource_info={selected_resource_info}
            disableInput={true}
            imageResizeEnabled={true}
          />
          <Box
            sx={{ display: 'flex', justifyContent: 'center', mt: pxToRem(10) }}
          >
            {assessment_content && (
              <PaginationButton
                currentPage={current_question_index + 1}
                totalPages={assessment_content?.assessmentContent.length || 0}
                onNextClick={onNextClick}
                // disabledPrevious={baseStationData?.sessionId ? true : false}
                onPreviousClick={onPreviousClick}
                disabledPrevious={true}
                disabledNext={
                  loading === 'loading' ||
                  !isEffectivelyOnline ||
                  !isQuestionSessionReady
                }
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
